import { CurrencyEnumType } from "@ifgengineering/client-invest-sdk";
import { DealFundraisingCapacity } from "@state/dealCapacity";
import { ratePercent } from "./format";
import { LiveDealCard } from "./helpers";

export interface FundTenor {
  card: LiveDealCard;
  noticeMonths: number | null;
  rateLabel?: string;
  exitRateLabel?: string;
  isBestRate: boolean;
}

export interface LiveDealFundGroup {
  kind: "fund";
  key: string;
  investableId: string;
  name: string;
  currency: CurrencyEnumType;
  isaEligible: boolean;
  isLive: boolean;
  statusText: string;
  bestRateLabel?: string;
  tenors: FundTenor[];
}

export type LiveDealListItem =
  | { kind: "deal"; card: LiveDealCard }
  | LiveDealFundGroup;

const QUARTERS_TO_MONTHS = 3;

const parseRatePct = (label?: string): number | null => {
  if (!label) return null;
  const match = label.match(/([\d.]+)\s*%/);
  return match ? Number(match[1]) : null;
};

export const tenorNoticeMonths = (card: LiveDealCard): number | null => {
  if (card.deal.activePeriodRate?.noticeMonths != null) {
    return card.deal.activePeriodRate.noticeMonths;
  }
  const quarters = card.deal.periodDealConfig?.noticePeriods;
  return quarters != null ? quarters * QUARTERS_TO_MONTHS : null;
};

/** Payload rate first (real, member-uplifted), rate label as fallback. */
export const tenorRateLabel = (card: LiveDealCard): string | undefined => {
  const payloadRate = card.deal.activePeriodRate?.standardRate;
  if (payloadRate != null) return ratePercent(payloadRate);
  return card.yieldLabel;
};

export const tenorExitRateLabel = (
  card: LiveDealCard
): string | undefined => {
  const exitRate = card.deal.activePeriodRate?.exitRate;
  return exitRate != null ? ratePercent(exitRate) : undefined;
};

/** Ascending by notice length, with exactly one tenor flagged as best rate. */
export const buildTenors = (cards: LiveDealCard[]): FundTenor[] => {
  const tenors = cards
    .map((card) => ({
      card,
      noticeMonths: tenorNoticeMonths(card),
      rateLabel: tenorRateLabel(card),
      exitRateLabel: tenorExitRateLabel(card),
      isBestRate: false,
    }))
    .sort(
      (a, b) =>
        (a.noticeMonths ?? Number.MAX_SAFE_INTEGER) -
        (b.noticeMonths ?? Number.MAX_SAFE_INTEGER)
    );

  const parsed = tenors.map((tenor) => parseRatePct(tenor.rateLabel));
  const best = parsed.reduce<number | null>(
    (max, rate) => (rate != null && (max == null || rate > max) ? rate : max),
    null
  );
  if (best != null) {
    const bestIndex = parsed.findIndex((rate) => rate === best);
    if (bestIndex >= 0) tenors[bestIndex].isBestRate = true;
  }
  return tenors;
};

const buildFundGroup = (
  investableId: string,
  cards: LiveDealCard[]
): LiveDealFundGroup => {
  const tenors = buildTenors(cards);
  const liveTenors = tenors.filter((tenor) => tenor.card.isLive);
  const statusCard = liveTenors[0]?.card ?? tenors[0].card;
  const best = tenors.find((tenor) => tenor.isBestRate);

  return {
    kind: "fund",
    key: `fund:${investableId}`,
    investableId,
    name: cards[0].deal.investable?.name ?? cards[0].name,
    currency: cards[0].currency,
    isaEligible: tenors.some((tenor) => tenor.card.isaEligible),
    isLive: liveTenors.length > 0,
    statusText: statusCard.statusText,
    bestRateLabel: best?.rateLabel,
    tenors,
  };
};

/**
 * Collapses cards that are tenors of one fund (usesPeriods deals sharing an
 * investable) into a single fund item. Groups need at least two members — a
 * lone tenor renders as a normal deal card. Order is preserved; a group takes
 * the list position of its first member.
 */
export const groupLiveDealCards = (
  cards: LiveDealCard[]
): LiveDealListItem[] => {
  const byInvestable = new Map<string, LiveDealCard[]>();
  for (const card of cards) {
    const investableId = card.deal.investable?.id;
    if (card.deal.usesPeriods === true && investableId) {
      const bucket = byInvestable.get(String(investableId)) ?? [];
      bucket.push(card);
      byInvestable.set(String(investableId), bucket);
    }
  }

  const groupedIds = new Set(
    [...byInvestable.entries()]
      .filter(([, bucket]) => bucket.length >= 2)
      .flatMap(([, bucket]) => bucket.map((card) => card.id))
  );

  const emitted = new Set<string>();
  const items: LiveDealListItem[] = [];
  for (const card of cards) {
    if (!groupedIds.has(card.id)) {
      items.push({ kind: "deal", card });
      continue;
    }
    const investableId = String(card.deal.investable?.id);
    if (emitted.has(investableId)) continue;
    emitted.add(investableId);
    items.push(buildFundGroup(investableId, byInvestable.get(investableId)!));
  }
  return items;
};

/** Notice range across a fund's tenors, e.g. "3 – 12 months". */
export const noticeRangeLabel = (
  tenors: FundTenor[]
): string | undefined => {
  const months = tenors
    .map((tenor) => tenor.noticeMonths)
    .filter((month): month is number => month != null);
  if (months.length === 0) return undefined;
  const min = Math.min(...months);
  const max = Math.max(...months);
  return min === max ? `${min} months` : `${min} – ${max} months`;
};

/**
 * One gauge for a fund whose tenors each run their own round: open rounds are
 * summed, and the fund only reads as closed when every round it has is closed.
 */
export const aggregateFundCapacity = (
  capacities: (DealFundraisingCapacity | undefined)[]
): DealFundraisingCapacity | undefined => {
  const present = capacities.filter(
    (capacity): capacity is DealFundraisingCapacity => capacity != null
  );
  if (present.length === 0) return undefined;

  const open = present.filter((capacity) => capacity.windowStart != null);
  if (open.length === 0) {
    return {
      fundraisingCap: null,
      remainingCapacity: null,
      capacityState: null,
      windowStart: null,
    };
  }

  const capped = open.filter((capacity) => capacity.fundraisingCap != null);
  if (capped.length === 0) return undefined;

  return {
    fundraisingCap: capped.reduce(
      (total, capacity) => total + (capacity.fundraisingCap ?? 0),
      0
    ),
    remainingCapacity: capped.reduce(
      (total, capacity) => total + Math.max(capacity.remainingCapacity ?? 0, 0),
      0
    ),
    capacityState: capped.some((capacity) => capacity.capacityState === "FULL")
      ? "FULL"
      : (capped[0].capacityState ?? null),
    windowStart: open[0].windowStart,
  };
};
